import React, { useEffect, useRef, useState } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import moment from "moment-timezone";
import { cqAPI } from "../../lib/Auth";

// SS-4400: where collecting a NEW patient's dental insurance before their visit stands (api:
// GET /patients/:id/insurance-collection), in plain words for the office: asked (when, how),
// waiting on the patient, verified, or needs staff, with the reason. Renders nothing when the
// patient has no collection, or when the api does not offer it (yet).

const FIELD_TEXT = {
  carrier_name: "dental insurance company",
  member_id: "member ID",
  group_number: "group number",
  subscriber_first_name: "policy holder's first name",
  subscriber_last_name: "policy holder's last name",
  subscriber_dob: "policy holder's date of birth",
  subscriber_zip: "policy holder's ZIP code",
  patient_dob: "patient's date of birth",
  patient_name: "patient's name",
  card_photo: "a photo of the card",
  newer_dental_insurance: "newer dental insurance",
  dental_insurance: "a dental (not medical) card or insurer",
};
export const fieldsInWords = (list) => (list || []).map((f) => FIELD_TEXT[f] || f.replace(/_/g, " ")).join(", ");

const STATE_TEXT = {
  verified: "Verified",
  awaiting_result: "Checking: waiting for the insurer's full breakdown",
  outreach_due: "Asking the patient",
  waiting_on_patient: "Waiting on the patient",
  staff: "Needs staff",
  retry_later: "Checking again later",
  done: "Closed",
};

const REASON_TEXT = {
  missing_fields: (c) => `Missing: ${fieldsInWords(c.missing)}.`,
  invalid_fields: (c) => `Not valid: ${fieldsInWords(c.missing)}.`,
  not_dental: () => "The patient sent insurance that is not dental (e.g. a medical card).",
  payer_unresolved: () => "The dental insurance company could not be identified.",
  member_not_found: () => "The insurer could not find these details; the patient is asked to confirm the member ID, the policy holder's name and date of birth, or to send a photo of the card.",
  validation_failed: (c) => (c.missing?.length
    ? `The insurer refused some details: ${fieldsInWords(c.missing)}.`
    : "The insurance network refused the request; review the details."),
  inactive_coverage: () => "The insurer says this plan is not active; the patient is asked whether they have newer dental insurance.",
  patient_not_eligible: () => "The insurer says the patient is not eligible.",
  third_party_administrator_required: () => "The plan is run by a third-party administrator; verify it with the administrator.",
  patient_data_suppressed: () => "The insurer does not release this patient's data electronically; verify it by phone.",
  unsupported_payer: () => "The insurer is not supported for electronic checks; verify it by phone.",
  upstream_unavailable: () => "The insurer's system was unavailable; Viva will try again.",
  no_credits: () => "The practice had no insurance verification credits for the automatic check, so it was not run.",
  office_setup_incomplete: () => "The practice's insurance verification setup is incomplete, so the check could not run.",
  verification_error: () => "The insurance check could not be completed; review the details.",
  max_asks: (c) => `The patient was asked the most times allowed without a verifiable answer.${c.missing?.length ? ` Still missing: ${fieldsInWords(c.missing)}.` : ""}`,
  verification_off: () => "Insurance verification was turned off.",
  appointment_not_live: () => "The appointment was cancelled, removed or has already taken place.",
  rescheduled: () => "The appointment was rescheduled.",
  details_changed: () => "The patient sent new details while a check ran; Viva checks the newest ones next.",
  opted_out: () => "The patient opted out of being contacted this way, so Viva stopped asking.",
  no_time_left: (c) => `No further ask fits before the visit (asks go out 9 AM-7 PM, Monday-Saturday, and calls never on the day of the visit).${c.missing?.length ? ` Still missing: ${fieldsInWords(c.missing)}.` : ""}`,
  patient_unreachable: (c) => `Viva could not reach the patient to ask (no usable phone number, texts to it bounced, or they opted out).${c.missing?.length ? ` Still missing: ${fieldsInWords(c.missing)}.` : ""}`,
};

const CHANNEL_TEXT = { text: "text", call: "phone call" };
// states the server moves on its own, with no message in the conversation (a portal result, a retry, an ask
// sent or skipped by the outreach job, a follow-up): read again while one is shown. verified, staff and done are final
const PENDING = ["outreach_due", "waiting_on_patient", "awaiting_result", "retry_later"];
export const PENDING_REFRESH_MS = 60 * 1000;
// a final status can still be reopened (a rescheduled visit is checked again): read it again, far less often
export const FINAL_REFRESH_MS = 15 * 60 * 1000;
const when = (at) => moment(at).format("MMM D, h:mm A");

// what became of the last ask (campaigns-sender and Ezra record it on last_ask.result)
const ASK_RESULT = {
  sent: (how, at) => `Last asked by ${how} on ${at}`,
  in_conversation: (how, at) => `Last asked by ${how}, in the conversation, on ${at}`,
  claimed: (how, at) => `Asking by ${how} since ${at} (being sent)`,
  // (`at` is when the outreach job found it so, not when the ask was due)
  skipped_no_contact: (how, at) => `The ${how} was not sent (${at}): the patient cannot be reached that way (opted out, no number, or the number bounced)`,
  skipped_too_late: (how, at) => `The ${how} was not made (${at}): it was too late (a call never falls on the day of the visit)`,
};

/** One collection → the lines the office reads. */
export const describeCollection = (c) => {
  const status = STATE_TEXT[c.state] || c.state;
  const lines = [];
  if (c.state === "outreach_due" && c.due_at) lines.push(`Next ask by ${CHANNEL_TEXT[c.channel] || c.channel} on ${when(c.due_at)}.`);
  if (c.state !== "verified" && c.reason && REASON_TEXT[c.reason]) lines.push(REASON_TEXT[c.reason](c));
  if (c.state === "verified" && (c.mismatches || []).length) {
    lines.push(`The insurer's record differs from what was sent: ${c.mismatches.map((m) => String(m?.field || m).replace(/_/g, " ")).join(", ")}.`);
  }
  if (c.portal_to_connect) lines.push(`Connect the ${c.portal_to_connect} portal in Insurance verification setup to get full breakdowns for this insurer.`);
  const asks = c.asks || {};
  const how = CHANNEL_TEXT[c.last_ask?.channel] || c.last_ask?.channel;
  const asked = c.last_ask?.at
    ? `${(ASK_RESULT[c.last_ask.result] || ((h, at) => `Last ask by ${h} on ${at}`))(how, when(c.last_ask.at))} (${asks.text || 0} by text, ${asks.call || 0} by call).`
    : "";
  return { status, lines, asked };
};

/** The newest message's id (a full page keeps its length when a new message comes in, so the count would not change). */
export const newestMessageKey = (messages) => {
  let newest = null;
  for (const m of messages || []) {
    if (m && (!newest || new Date(m.created || 0) >= new Date(newest.created || 0))) newest = m;
  }
  return newest?._id ? String(newest._id) : "";
};

/** The collection to show: the next visit's still-open one, else the next visit's, else the latest one. */
export const pickCollection = (list, now = new Date()) => {
  if (!Array.isArray(list) || !list.length) return null;
  const upcoming = list.filter((c) => new Date(c.appointment_start) >= now)
    .sort((a, b) => new Date(a.appointment_start) - new Date(b.appointment_start));
  // a closed one (e.g. a cancelled visit) never hides another visit's that still needs something
  return upcoming.find((c) => c.state !== "done") || upcoming[0] || list[0];
};

/**
 * `refreshKey` changes when the conversation does (a new message): the status is read again, so a card
 * the patient just sent shows as it now stands.
 */
function InsuranceCollectionStatus({ uid, refreshKey = 0 }) {
  // held with the patient it was read for: another patient's status is never shown, not even for one render
  const [loaded, setLoaded] = useState({ uid: null, collection: null });
  const [tick, setTick] = useState(0);
  const border = useColorModeValue("#E5E5EF", "#64728C");
  const muted = useColorModeValue("gray.600", "gray.300");

  // the newest read for this patient, so a failed re-read still knows whether the status is pending
  const latest = useRef({ uid: null, collection: null });
  useEffect(() => {
    let live = true;
    let timer = null;
    if (!uid) return undefined;
    // (from a resolved promise: whatever the client returns or throws, the read settles and the panel goes on)
    Promise.resolve()
      .then(() => cqAPI.get(`/patients/${uid}/insurance-collection`))
      .then((resp) => {
        if (live && resp?.type === "ok") {
          latest.current = { uid, collection: pickCollection(resp.data) };
          setLoaded(latest.current);
        }
      })
      .catch(() => { /* the api does not offer it (yet): show nothing */ })
      .finally(() => {
        // a pending status changes without any new message (e.g. the insurer's portal answers): read it again a
        // minute after this read SETTLED (a slow read is never cut short), a failed one too, while it stays pending
        // (no collection yet: the sweep may start one without any message, so it is read again too)
        if (live) {
          const shown = latest.current.uid === uid ? latest.current.collection : null;
          const wait = shown && PENDING.includes(shown.state) ? PENDING_REFRESH_MS : FINAL_REFRESH_MS;
          timer = setTimeout(() => setTick((t) => t + 1), wait);
        }
      });
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [uid, refreshKey, tick]);

  const collection = loaded.uid === uid ? loaded.collection : null;
  if (!collection) return null;
  const { status, lines, asked } = describeCollection(collection);
  return (
    <Box data-testid="insurance-collection-status" borderWidth="1px" borderColor={border} borderRadius="md" p={2} mx={2} mt={3} width="100%">
      <Text fontSize="sm" fontWeight="semibold">Insurance before the visit</Text>
      <Text fontSize="xs" color={muted}>For the appointment on {moment(collection.appointment_start).format("ddd, MMM D, h:mm A")}</Text>
      <Text fontSize="sm" mt={1} data-testid="insurance-collection-state">{status}</Text>
      {lines.map((line) => <Text key={line} fontSize="xs">{line}</Text>)}
      {asked && <Text fontSize="xs" color={muted} data-testid="insurance-collection-asked">{asked}</Text>}
    </Box>
  );
}

export default InsuranceCollectionStatus;
